import type { ListQuery } from '../../administration/users/userTypes';

/*
  THE WIRE ENUMS ARE NUMERIC — no string converter is configured — so each is an `as const`
  object of numbers mirroring the backend member for member. A member added there is added here
  in the same change, or it falls through every label to the default and is described wrongly.
*/

/** `CaseStatus` — the bureau's three words, in its order. */
export const CASE_STATUS = { ongoing: 0, terminated: 1, accomplished: 2 } as const;
export type CaseStatus = (typeof CASE_STATUS)[keyof typeof CASE_STATUS];

export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  0: 'Ongoing',
  1: 'Terminated',
  2: 'Accomplished',
};

/**
 * `RepositoryPermissions` — the five independent flags a case member holds, the matrix
 * Repository's share dialog draws. Powers of two, persisted as the number. A member's flags on the
 * CASE are their flags on everything filed under it.
 */
export const FLAG = { view: 1, share: 2, upload: 4, edit: 8, delete: 16 } as const;
export const ALL_FLAGS = FLAG.view | FLAG.share | FLAG.upload | FLAG.edit | FLAG.delete;

export function hasFlag(flags: number | null | undefined, flag: number): boolean {
  return ((flags ?? 0) & flag) === flag;
}

/** Anything ticked implies View — the one implicit relationship, mirrored from the domain. */
export function normaliseFlags(flags: number): number {
  return flags === 0 ? 0 : flags | FLAG.view;
}

/** The flags in the order the share matrix draws them, with its words. */
export const FLAG_NAMES: [number, string][] = [
  [FLAG.view, 'View'],
  [FLAG.share, 'Share'],
  [FLAG.upload, 'Upload'],
  [FLAG.edit, 'Edit'],
  [FLAG.delete, 'Delete'],
];

/**
 * Every entity type the registry writes — what its History Logs read. A RENAME keeps the old CLR
 * name on this list forever: the case's rows written before 12 Oct 2026 carry `EvidenceCase`.
 */
export const CASE_AUDIT_ENTITY_TYPES = ['Case', 'CaseMember', 'CaseAttachment', 'EvidenceCase', 'EvidenceCaseMember'];

/** What a report's History Logs read — the report and its files. */
export const POR_AUDIT_ENTITY_TYPES = ['PostOperationReport', 'PostOperationReportAttachment'];

export interface CaseListItem {
  id: string;
  caseNumber: string;
  /** `********` on a restricted row — fixed length, so it says nothing about the title it hides. */
  title: string;
  status: CaseStatus;
  accessLevelId: string | null;
  accessLevelName: string | null;
  accessLevelRank: number | null;
  isRestricted: boolean;
  /** The caller's flags on this case — `FLAG`. Resolved server-side for the caller. */
  permissions: number;
  /** Live evidence items not disposed of — what the server's delete refusal counts. */
  heldEvidenceCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Whether the caller's account is linked to an employee — the registry's employee rule. */
export interface CaseEligibility {
  isEmployee: boolean;
}

export interface CaseListQuery extends ListQuery {
  caseStatus?: string;
}

export interface CreateCaseRequest {
  caseNumber: string;
  title: string;
  status: CaseStatus;
  accessLevelId: string | null;
}

export interface CaseRequest {
  caseNumber: string;
  title: string;
  status: CaseStatus;
}

export interface CaseMemberRow {
  userId: string;
  displayName: string;
  email: string | null;
  personnelId: string;
  employeeNumber: string;
  permissions: number;
}

export interface CaseMemberSummary {
  canManage: boolean;
  /** The caller's own flags — a non-creator grants only these, never edits their own row or a stronger one. */
  callerPermissions: number;
  callerUserId: string;
  creatorUserId: string | null;
  creatorName: string | null;
  members: CaseMemberRow[];
}

export interface CaseMemberOption {
  userId: string;
  displayName: string;
  email: string | null;
  employeeNumber: string;
}

// ── Files ───────────────────────────────────────────────────────────────────────────────

/** One file filed with a case or a report. The type is the one the server VERIFIED by its bytes. */
export interface CaseAttachmentItem {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** The picker filter — a convenience; the server checks the bytes. The two categories share it. */
export const CASE_FILE_ACCEPT = '.pdf,.png,.jpg,.jpeg,.mp4,application/pdf,image/png,image/jpeg,video/mp4';

/** The server's per-file caps — `CaseFieldRules` / `PostOperationReportFieldRules`. */
export const CASE_FILE_MAX_BYTES = 100 * 1024 * 1024;
export const REPORT_FILE_MAX_BYTES = 250 * 1024 * 1024;

// ── Post Operation Reports ──────────────────────────────────────────────────────────────

export interface PostOperationReportListItem {
  id: string;
  caseId: string;
  caseNumber: string;
  title: string;
  /** Plain text for now — the rich-text editor was deferred (12 Oct 2026). */
  summary: string;
  attachmentCount: number;
  /** The caller's flags on the CASE — what the report's controls are disabled by. */
  permissions: number;
  caseIsActive: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface PostOperationReportRequest {
  title: string;
  summary: string;
}
